import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  TextField,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  FormHelperText,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import { useLead } from '../../contexts/LeadContext';
import { useModal } from '../../contexts/ModalContext';
import AgentSelect from './AgentSelect';
import { fetchAgentSelect } from '../../services/common';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const outlineLegendNoGap = {
  '& .MuiOutlinedInput-notchedOutline legend': {
    maxWidth: '0 !important',
    width: '0 !important',
    fontSize: 0,
    lineHeight: 0,
    padding: 0,
  },
  '& .MuiOutlinedInput-notchedOutline legend span': {
    padding: '0 !important',
    width: '0 !important',
    minWidth: '0 !important',
    display: 'block',
  },
};

const fieldSx = {
  ...outlineLegendNoGap,
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
    '&.Mui-error fieldset': { borderColor: T.red },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    overflow: 'visible',
    '&.Mui-focused': { color: T.green },
  },
  '& .MuiFormHelperText-root': { fontFamily: T.font, fontSize: 11.5, mt: 0.5 },
  '& .MuiOutlinedInput-input::placeholder': { fontFamily: T.font, opacity: 0.55 },
};

const sectionLabelSx = {
  fontFamily: T.font,
  fontSize: 11,
  fontWeight: 700,
  color: T.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  mb: 1,
};

const AssignAgent = ({
  open,
  onClose,
  selectedLeadIds = [],
  currentAgent = null,
  onAssign = () => {},
}) => {
  const { assignAgents } = useLead();
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { openSuccessModal } = useModal();
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [fetchingAgents, setFetchingAgents] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState(null);
  const [notes, setNotes] = useState('');
  const [notesError, setNotesError] = useState('');
  const [isReassignment, setIsReassignment] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const filteredAgents = useMemo(() => {
    if (!currentAgent || !agents.length) return agents;
    return agents.filter(
      (agent) =>
        !(
          (agent.agent_id && agent.agent_id === currentAgent.id) ||
          (agent.agent_id && agent.agent_id === currentAgent.agent_id)
        )
    );
  }, [agents, currentAgent]);

  const checkReassignment = useCallback(() => {
    if (!open || selectedLeadIds.length === 0) return false;
    if (currentAgent) return true;
    return leads.some(
      (lead) =>
        selectedLeadIds.includes(lead.id) && (lead.agent_id || lead.agent_name)
    );
  }, [open, selectedLeadIds, currentAgent, leads]);

  useEffect(() => {
    if (!open) setHasFetched(false);
  }, [open]);

  useEffect(() => {
    if (!open || hasFetched) return;

    const initializeData = async () => {
      try {
        setFetchingAgents(true);
        const agentsData = await fetchAgentSelect();
        setAgents(agentsData);
        setLeads([]);
        setIsReassignment(checkReassignment());
        if (currentAgent && agentsData.length > 0) {
          const foundAgent = agentsData.find(
            (agent) =>
              agent.agent_id === currentAgent.id ||
              agent.agent_id === currentAgent.agent_id
          );
          if (foundAgent) setSelectedAgent(foundAgent);
        } else if (!currentAgent) {
          setSelectedAgent(null);
        }
        setHasFetched(true);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setFetchingAgents(false);
      }
    };

    setAssignmentResult(null);
    setNotes('');
    setNotesError('');
    initializeData();
  }, [open, hasFetched, currentAgent?.id, currentAgent?.agent_id, checkReassignment]);

  useEffect(() => {
    if (open && leads.length > 0) {
      setIsReassignment(checkReassignment());
    }
  }, [leads, selectedLeadIds, checkReassignment, open]);

  const validateNotes = useCallback(() => {
    if (isReassignment) {
      if (!notes || notes.trim() === '') {
        setNotesError('Notes are required when reassigning an agent');
        return false;
      }
      if (notes.trim().length < 20) {
        setNotesError(
          `Notes must be at least 20 characters (currently ${notes.trim().length})`
        );
        return false;
      }
    }
    setNotesError('');
    return true;
  }, [isReassignment, notes]);

  const handleAssign = async () => {
    if (!selectedAgent || selectedLeadIds.length === 0) return;
    if (isReassignment && !validateNotes()) return;

    try {
      setIsLoading(true);
      setAssignmentResult(null);
      const assignmentData = {
        lead_ids: selectedLeadIds,
        agent_id: selectedAgent.id || selectedAgent.agent_id,
        notes: notes.trim(),
      };
      const result = await assignAgents(assignmentData);

      if (!result.errored || result.errored.length === 0) {
        onAssign(selectedAgent.id || selectedAgent.agent_id);
        setSelectedAgent(null);
        setNotes('');
        onClose();
        const agentName = selectedAgent.name || selectedAgent.agent_name;
        const successTitle = isReassignment ? 'Agent reassigned' : 'Agent assigned';
        const successMessage =
          selectedLeadIds.length > 1
            ? `Successfully assigned ${agentName} to ${result.passed.length} leads.`
            : `Successfully assigned ${agentName} to the lead.`;
        openSuccessModal(successTitle, successMessage);
      } else if (result.errored && result.errored.length > 0) {
        if (result.passed && result.passed.length > 0) {
          const agentName = selectedAgent.name || selectedAgent.agent_name;
          openSuccessModal(
            'Partial success',
            `Assigned ${agentName} to ${result.passed.length} lead(s), but failed for ${result.errored.length} lead(s).`
          );
          onClose();
        } else {
          setAssignmentResult(result);
        }
      }
    } catch (error) {
      console.error('Error assigning agent:', error);
      setAssignmentResult({
        error: true,
        message: error.message || 'Failed to assign agent',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    setSelectedAgent(null);
    setAssignmentResult(null);
    setNotes('');
    setNotesError('');
    onClose();
  }, [onClose]);

  const handleNotesChange = useCallback((e) => {
    const value = e.target.value;
    setNotes(value);
    if (value.trim().length >= 20) setNotesError('');
  }, []);

  const dialogTitle =
    selectedLeadIds.length > 1
      ? isReassignment
        ? 'Reassign agent for selected leads'
        : 'Assign agent to selected leads'
      : currentAgent
        ? 'Reassign agent'
        : 'Assign agent to lead';

  const dialogSubtitle =
    selectedLeadIds.length > 1
      ? isReassignment
        ? 'Pick a new agent for all selected leads. Notes are required.'
        : 'Choose one agent to apply to all selected leads.'
      : isReassignment
        ? 'Select a new agent and document why you’re reassigning.'
        : 'Choose who will own this lead.';

  const HeaderIcon = isReassignment ? SwapHorizOutlinedIcon : PersonAddOutlinedIcon;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 480,
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(15,26,19,0.14), 0 2px 8px rgba(15,26,19,0.06)',
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.25,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '12px',
              bgcolor: T.greenLt,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <HeaderIcon sx={{ fontSize: 22, color: T.green }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: 17,
                fontWeight: 700,
                color: T.ink,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
              }}
            >
              {dialogTitle}
            </Typography>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: 12,
                color: T.muted,
                mt: 0.15,
                lineHeight: 1.5,
              }}
            >
              {dialogSubtitle}
            </Typography>
            {selectedLeadIds.length > 1 && (
              <Chip
                size="small"
                label={`${selectedLeadIds.length} leads`}
                sx={{
                  mt: 1,
                  fontFamily: T.font,
                  fontSize: 11,
                  fontWeight: 600,
                  height: 24,
                  bgcolor: T.bg,
                  color: T.subtle,
                  border: `1px solid ${T.borderL}`,
                }}
              />
            )}
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close"
          disabled={isLoading}
          sx={{
            color: T.muted,
            mt: 0.25,
            '&:hover': { color: T.ink, bgcolor: T.greenSf },
            flexShrink: 0,
            borderRadius: 2,
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {fetchingAgents ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} thickness={4} sx={{ color: T.green }} />
            </Box>
          ) : (
            <>
              <Box>
                <Typography sx={sectionLabelSx}>
                  {isReassignment ? 'New assignee' : 'Agent / manager'}
                </Typography>
                <AgentSelect
                  agents={filteredAgents}
                  value={selectedAgent ? selectedAgent.id || selectedAgent.agent_id : ''}
                  onChange={(id, agentObj) => setSelectedAgent(agentObj)}
                  label=""
                  hideInputLabel
                  inputPlaceholder="Search agents…"
                  name="agent"
                  required
                  error=""
                  size="small"
                  variant={isReassignment ? 'reassign' : 'assign'}
                  multiple={false}
                  showAvatar
                  clearable={false}
                  sx={outlineLegendNoGap}
                />
              </Box>

              {isReassignment && (
                <Box>
                  <Typography sx={sectionLabelSx}>Reassignment notes</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    hiddenLabel
                    placeholder="Explain why you’re reassigning (minimum 20 characters)…"
                    value={notes}
                    onChange={handleNotesChange}
                    onBlur={validateNotes}
                    error={!!notesError}
                    sx={fieldSx}
                    slotProps={{ input: { notched: false } }}
                    InputProps={{ notched: false }}
                  />
                  {notesError && (
                    <FormHelperText error sx={{ fontFamily: T.font, fontSize: 11.5, mt: 0.5 }}>
                      {notesError}
                    </FormHelperText>
                  )}
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontSize: 11,
                      color: notes.trim().length >= 20 ? T.green : T.muted,
                      mt: 0.75,
                      fontWeight: 500,
                    }}
                  >
                    {notes.trim().length} / 20 characters minimum
                  </Typography>
                </Box>
              )}
            </>
          )}

          {assignmentResult && assignmentResult.errored && assignmentResult.errored.length > 0 && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${T.redBorder}`,
                bgcolor: T.redLt,
              }}
            >
              <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red, fontWeight: 600 }}>
                Could not assign {assignmentResult.errored.length} lead
                {assignmentResult.errored.length !== 1 ? 's' : ''}
              </Typography>
              <Box sx={{ mt: 1, maxHeight: 100, overflow: 'auto' }}>
                {assignmentResult.errored.map((item) => (
                  <Typography
                    key={item.id}
                    sx={{
                      fontFamily: T.font,
                      fontSize: 11.5,
                      color: T.red,
                      display: 'block',
                      py: 0.25,
                    }}
                  >
                    Lead #{item.id}: {item.message || 'Unknown error'}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assignmentResult && assignmentResult.error && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${T.redBorder}`,
                bgcolor: T.redLt,
              }}
            >
              <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red, fontWeight: 500 }}>
                {assignmentResult.message || 'An error occurred during assignment'}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={handleClose}
          size="small"
          disabled={isLoading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.white,
            bgcolor: T.red,
            boxShadow: 'none',
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            '&:hover': { bgcolor: T.redMd, boxShadow: 'none', color: T.white },          }}
        >
          {assignmentResult && assignmentResult.errored && assignmentResult.errored.length > 0
            ? 'Close'
            : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          size="small"
          disabled={
            !selectedAgent ||
            isLoading ||
            selectedLeadIds.length === 0 ||
            (isReassignment && (!notes || notes.trim().length < 20))
          }
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            minWidth: 120,
            height: 38,
            bgcolor: T.green,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} thickness={4} sx={{ color: '#fff' }} />
          ) : isReassignment ? (
            'Reassign'
          ) : (
            'Assign'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignAgent;
